import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { fmtAUD } from '@/lib/format';
import { toast, Toaster } from 'sonner';
import { Save, TrendingUp } from 'lucide-react';

const numOrEmpty = (v) => (v === null || v === undefined ? '' : String(v));

export default function AdminPricing() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [edits, setEdits] = useState({});

    const reload = async () => {
        const r = await api.get('/products');
        setProducts(r.data);
    };

    useEffect(() => {
        reload().finally(() => setLoading(false));
    }, []);

    const setField = (slug, key, val) => {
        setEdits((e) => ({ ...e, [slug]: { ...(e[slug] || {}), [key]: val } }));
    };

    const save = async (slug) => {
        const draft = edits[slug];
        if (!draft) return;
        const body = {};
        for (const f of ['price', 'rrp', 'landed_cost']) {
            if (draft[f] !== undefined) {
                const n = parseFloat(draft[f]);
                body[f] = Number.isNaN(n) ? 0 : n;
            }
        }
        try {
            const r = await api.patch(`/products/${slug}`, body);
            setProducts((prev) => prev.map((p) => (p.slug === slug ? r.data : p)));
            setEdits((e) => { const c = { ...e }; delete c[slug]; return c; });
            toast.success(`Updated ${r.data.name}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Update failed');
        }
    };

    const computed = useMemo(() => products.map((p) => {
        const d = edits[p.slug] || {};
        const landed = parseFloat(d.landed_cost !== undefined ? d.landed_cost : (p.landed_cost ?? 0)) || 0;
        const price = parseFloat(d.price !== undefined ? d.price : (p.price ?? 0)) || 0;
        const rrp = parseFloat(d.rrp !== undefined ? d.rrp : (p.rrp ?? 0)) || 0;
        const hq_margin = landed > 0 ? ((price - landed) / landed) * 100 : 0;
        const academy_margin = price > 0 && rrp > 0 ? ((rrp - price) / price) * 100 : 0;
        const dirty = Object.keys(d).length > 0;
        return { p, landed, price, rrp, hq_margin, academy_margin, dirty };
    }), [products, edits]);

    const totals = useMemo(() => {
        return computed.reduce((acc, r) => ({
            landed: acc.landed + r.landed,
            price: acc.price + r.price,
            rrp: acc.rrp + r.rrp,
        }), { landed: 0, price: 0, rrp: 0 });
    }, [computed]);

    if (loading) return <div className="text-zinc-400 font-mono-data">Loading…</div>;

    return (
        <div data-testid="admin-pricing">
            <Toaster theme="dark" />

            <section className="grid grid-cols-2 md:grid-cols-4 border-hairline mb-8">
                <div className="p-5 border-r border-zinc-800">
                    <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">Sum of landed costs</div>
                    <div className="font-display text-2xl text-amber-400">{fmtAUD(totals.landed)}</div>
                </div>
                <div className="p-5 border-r border-zinc-800">
                    <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">Sum of academy wholesale</div>
                    <div className="font-display text-2xl text-brand">{fmtAUD(totals.price)}</div>
                </div>
                <div className="p-5 border-r border-zinc-800">
                    <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">Sum of suggested RRP</div>
                    <div className="font-display text-2xl">{fmtAUD(totals.rrp)}</div>
                </div>
                <div className="p-5">
                    <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">HQ → Academy margin</div>
                    <div className="font-display text-2xl text-brand flex items-center gap-2">
                        <TrendingUp size={20} />
                        {totals.landed > 0
                            ? `${(((totals.price - totals.landed) / totals.landed) * 100).toFixed(0)}%`
                            : '—'}
                    </div>
                </div>
            </section>

            <section className="border-hairline">
                <div className="grid grid-cols-12 font-mono-data text-[10px] uppercase text-zinc-500 px-5 py-3 border-b border-zinc-800 bg-surface">
                    <div className="col-span-3">Product</div>
                    <div className="col-span-2 text-right">HQ Landed (cost)</div>
                    <div className="col-span-2 text-right">Academy Wholesale</div>
                    <div className="col-span-2 text-right">Member RRP</div>
                    <div className="col-span-1 text-right">HQ Margin</div>
                    <div className="col-span-1 text-right">Acad Margin</div>
                    <div className="col-span-1 text-right">Action</div>
                </div>
                {computed.map(({ p, landed, price, rrp, hq_margin, academy_margin, dirty }) => (
                    <div key={p.slug} data-testid={`pricing-row-${p.slug}`}
                         className="grid grid-cols-12 items-center px-5 py-4 border-b border-zinc-800/60 hover:bg-surface/40">
                        <div className="col-span-3 flex items-center gap-3">
                            <img src={p.image_url} alt="" className="w-12 h-12 object-cover border border-zinc-800 bg-[#e9e7e6]" />
                            <div>
                                <div className="font-display uppercase text-sm tracking-tight">{p.name}</div>
                                <div className="font-mono-data text-[10px] text-zinc-500">{p.slug}</div>
                            </div>
                        </div>
                        <div className="col-span-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                                <span className="font-mono-data text-xs text-zinc-500">A$</span>
                                <input
                                    data-testid={`landed-input-${p.slug}`}
                                    type="number" step="0.01" min="0"
                                    value={numOrEmpty(edits[p.slug]?.landed_cost ?? p.landed_cost)}
                                    onChange={(e) => setField(p.slug, 'landed_cost', e.target.value)}
                                    className="w-24 bg-surface border border-zinc-800 px-2 py-1 text-right font-mono-data text-sm text-amber-400" />
                            </div>
                        </div>
                        <div className="col-span-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                                <span className="font-mono-data text-xs text-zinc-500">A$</span>
                                <input
                                    data-testid={`price-input-${p.slug}`}
                                    type="number" step="0.01" min="0"
                                    value={numOrEmpty(edits[p.slug]?.price ?? p.price)}
                                    onChange={(e) => setField(p.slug, 'price', e.target.value)}
                                    className="w-24 bg-surface border border-zinc-800 px-2 py-1 text-right font-mono-data text-sm text-brand" />
                            </div>
                        </div>
                        <div className="col-span-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                                <span className="font-mono-data text-xs text-zinc-500">A$</span>
                                <input
                                    data-testid={`rrp-input-${p.slug}`}
                                    type="number" step="0.01" min="0"
                                    value={numOrEmpty(edits[p.slug]?.rrp ?? p.rrp)}
                                    onChange={(e) => setField(p.slug, 'rrp', e.target.value)}
                                    className="w-24 bg-surface border border-zinc-800 px-2 py-1 text-right font-mono-data text-sm" />
                            </div>
                        </div>
                        <div className="col-span-1 text-right font-mono-data text-sm">
                            <span className={hq_margin > 0 ? 'text-brand' : 'text-zinc-500'}>
                                {landed > 0 ? `${hq_margin.toFixed(0)}%` : '—'}
                            </span>
                            <div className="font-mono-data text-[10px] text-zinc-500">
                                {landed > 0 ? `+${fmtAUD(price - landed)}` : ''}
                            </div>
                        </div>
                        <div className="col-span-1 text-right font-mono-data text-sm">
                            <span className={academy_margin > 0 ? 'text-brand' : 'text-zinc-500'}>
                                {rrp > 0 && price > 0 ? `${academy_margin.toFixed(0)}%` : '—'}
                            </span>
                            <div className="font-mono-data text-[10px] text-zinc-500">
                                {rrp > 0 ? `+${fmtAUD(rrp - price)}` : ''}
                            </div>
                        </div>
                        <div className="col-span-1 text-right">
                            {dirty && (
                                <button onClick={() => save(p.slug)}
                                        data-testid={`pricing-save-${p.slug}`}
                                        className="bg-brand hover:bg-brand-hover text-black px-3 py-1.5 inline-flex items-center gap-1">
                                    <Save size={12} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </section>

            <section className="mt-6 font-mono-data text-[10px] uppercase text-zinc-500 leading-relaxed max-w-3xl">
                HQ Landed = what Locals HQ pays the supplier (FOB + freight, all-in). Academy Wholesale = what each
                academy pays HQ. Member RRP = the price academies are encouraged to sell to their members. HQ Margin =
                (wholesale − landed) ÷ landed. Academy Margin = (RRP − wholesale) ÷ wholesale.
            </section>
        </div>
    );
}
